import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import EmojiPicker from 'emoji-picker-react'
import { apiFetch, apiUpload } from '../lib/api'

const QUICK_REACTIONS = ['❤️', '👍', '😂', '😮', '😢', '🙏']

function MessageBubble({ msg, isMine, otherUser, currentUserId, onReact, onReply, onOpenReel, isLastInGroup }) {
  const [showReactions, setShowReactions] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const reactRef = useRef(null)
  const menuRef = useRef(null)
  const longPressTimer = useRef(null)
  const touchStartX = useRef(null)
  const touchStartY = useRef(null)
  const swipeTriggered = useRef(false)
  const [swipeOffset, setSwipeOffset] = useState(0)

  const time = new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })

  const reactionGroups = (msg.reactions || []).reduce((acc, r) => {
    acc[r.emoji] = acc[r.emoji] || []
    acc[r.emoji].push(r.userId)
    return acc
  }, {})

  // Close popups on outside click
  useEffect(() => {
    if (!showReactions && !showMenu) return
    function handler(e) {
      if (reactRef.current && !reactRef.current.contains(e.target)) setShowReactions(false)
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showReactions, showMenu])

  // Mobile: long press → show reactions
  function handleTouchStart(e) {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    swipeTriggered.current = false
    longPressTimer.current = setTimeout(() => setShowReactions(true), 500)
  }

  // Mobile: swipe right → reply; slight swipe → visual feedback
  function handleTouchMove(e) {
    if (touchStartX.current === null) return
    const dx = e.touches[0].clientX - touchStartX.current
    const dy = e.touches[0].clientY - touchStartY.current
    if (Math.abs(dy) > 15) clearTimeout(longPressTimer.current)
    if (dx > 60 && !swipeTriggered.current && Math.abs(dy) < 30) {
      swipeTriggered.current = true
      clearTimeout(longPressTimer.current)
      setSwipeOffset(0)
      onReply(msg)
    } else if (dx > 0 && dx <= 60 && !swipeTriggered.current && Math.abs(dy) < 30) {
      setSwipeOffset(Math.min(dx * 0.5, 30))
    }
  }

  function handleTouchEnd() {
    clearTimeout(longPressTimer.current)
    setSwipeOffset(0)
    touchStartX.current = null
    touchStartY.current = null
  }

  // Whether to force the controls visible (popup is open)
  const controlsVisible = showReactions || showMenu

  return (
    <div className={`flex items-end gap-1 group ${isMine ? 'justify-end' : 'justify-start'}`}>

      {/* ── Controls left-of-bubble (my messages): [😊] [▾] ── */}
      {isMine && (
        <div className={`flex items-center gap-0.5 self-end pb-1 transition-opacity duration-150 ${controlsVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          {/* React (emoji) button */}
          <div className="relative" ref={reactRef}>
            <button
              onClick={() => { setShowMenu(false); setShowReactions(v => !v) }}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title="React"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.536-4.464a.75.75 0 10-1.061-1.061 3.5 3.5 0 01-4.95 0 .75.75 0 00-1.06 1.06 5 5 0 007.07 0zM9 8.5A.75.75 0 007.5 9.25a.75.75 0 001.5 0A.75.75 0 009 8.5zm2 .75a.75.75 0 101.5 0 .75.75 0 00-1.5 0z" clipRule="evenodd" />
              </svg>
            </button>
            {showReactions && (
              <div className="absolute bottom-full right-0 mb-1 z-20 flex items-center gap-1 bg-white rounded-full shadow-lg border border-gray-100 px-2 py-1.5">
                {QUICK_REACTIONS.map(e => (
                  <button key={e} onClick={() => { onReact(msg.id, e); setShowReactions(false) }}
                    className="text-lg hover:scale-125 transition-transform leading-none p-0.5">{e}</button>
                ))}
              </div>
            )}
          </div>
          {/* Down-arrow / options button */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => { setShowReactions(false); setShowMenu(v => !v) }}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title="Options"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            {showMenu && (
              <div className="absolute bottom-full right-0 mb-1 z-20 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden min-w-[110px]">
                <button onClick={() => { onReply(msg); setShowMenu(false) }}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors">
                  <span className="text-base">↩</span><span>Reply</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Bubble (with swipe handlers) ── */}
      <div
        className={`flex flex-col max-w-[72%] ${isMine ? 'items-end' : 'items-start'} relative`}
        style={{ transform: `translateX(${swipeOffset}px)`, transition: swipeOffset === 0 ? 'transform 0.2s ease' : 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Reply context strip */}
        {msg.replyTo && (
          <div className={`mb-0.5 px-3 py-1.5 rounded-xl text-xs max-w-full border-l-2 ${isMine ? 'bg-orange-100 border-orange-400 text-orange-800' : 'bg-gray-100 border-gray-400 text-gray-600'}`}>
            <span className="font-semibold block mb-0.5">
              {msg.replyTo.senderId === currentUserId ? 'You' : otherUser.name}
            </span>
            {msg.replyTo.mediaType === 'image' ? '📷 Photo' :
             msg.replyTo.mediaType === 'reel'  ? '🎬 Reel' :
             msg.replyTo.mediaType === 'file'  ? `📎 ${msg.replyTo.fileName || 'File'}` :
             <span className="truncate block max-w-[200px]">{msg.replyTo.text}</span>}
          </div>
        )}

        {/* Bubble */}
        <div className={`rounded-2xl text-sm leading-relaxed ${isMine ? 'bg-orange-500 text-white rounded-br-sm' : 'bg-white text-gray-900 shadow-sm rounded-bl-sm'} ${msg.mediaUrl && !msg.text ? 'p-1' : 'px-4 py-2'}`}>
          {msg.mediaType === 'image' && msg.mediaUrl && (
            <a href={msg.mediaUrl} target="_blank" rel="noreferrer">
              <img src={msg.mediaUrl} alt="image" className="rounded-xl max-w-[240px] max-h-[300px] object-cover cursor-pointer" />
            </a>
          )}
          {msg.mediaType === 'file' && msg.mediaUrl && (
            <a href={msg.mediaUrl} target="_blank" rel="noreferrer"
              className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isMine ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-100 hover:bg-gray-200'} transition`}>
              <span className="text-2xl">📎</span>
              <span className={`text-xs font-medium truncate max-w-[160px] ${isMine ? 'text-white' : 'text-gray-700'}`}>{msg.fileName || 'File'}</span>
              <span className={`text-xs shrink-0 ${isMine ? 'text-orange-200' : 'text-gray-400'}`}>↓</span>
            </a>
          )}
          {msg.mediaType === 'reel' && msg.mediaUrl && (
            <button
              type="button"
              onClick={() => onOpenReel?.(Number(msg.fileName) || null)}
              className={`block w-full overflow-hidden rounded-xl border text-left ${isMine ? 'border-orange-300 bg-orange-600/40' : 'border-gray-200 bg-gray-100'} transition hover:opacity-95`}
            >
              <div className="relative h-56 w-full bg-black">
                <video src={msg.mediaUrl} muted loop autoPlay playsInline className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
                <div className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-1 text-[11px] font-semibold text-white">🎬 Open Reel</div>
              </div>
            </button>
          )}
          {msg.text && <span className={msg.mediaUrl ? 'block px-2 pb-1 pt-1' : ''}>{msg.text}</span>}
        </div>

        {/* Reactions row */}
        {Object.keys(reactionGroups).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5 px-1">
            {Object.entries(reactionGroups).map(([emoji, userIds]) => {
              const iReacted = userIds.includes(currentUserId)
              return (
                <button key={emoji} onClick={() => onReact(msg.id, emoji)}
                  className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs border transition ${iReacted ? 'bg-orange-100 border-orange-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  <span>{emoji}</span>
                  {userIds.length > 1 && <span className="text-gray-600">{userIds.length}</span>}
                </button>
              )
            })}
          </div>
        )}

        <span className="mt-0.5 text-[10px] text-gray-400 px-1">{time}</span>
      </div>

      {/* ── Controls right-of-bubble (received messages): [▾] [😊] ── */}
      {!isMine && (
        <div className={`flex items-center gap-0.5 self-end pb-1 transition-opacity duration-150 ${controlsVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          {/* Down-arrow / options button */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => { setShowReactions(false); setShowMenu(v => !v) }}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title="Options"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            {showMenu && (
              <div className="absolute bottom-full left-0 mb-1 z-20 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden min-w-[110px]">
                <button onClick={() => { onReply(msg); setShowMenu(false) }}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors">
                  <span className="text-base">↩</span><span>Reply</span>
                </button>
              </div>
            )}
          </div>
          {/* React (emoji) button */}
          <div className="relative" ref={reactRef}>
            <button
              onClick={() => { setShowMenu(false); setShowReactions(v => !v) }}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title="React"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.536-4.464a.75.75 0 10-1.061-1.061 3.5 3.5 0 01-4.95 0 .75.75 0 00-1.06 1.06 5 5 0 007.07 0zM9 8.5A.75.75 0 007.5 9.25a.75.75 0 001.5 0A.75.75 0 009 8.5zm2 .75a.75.75 0 101.5 0 .75.75 0 00-1.5 0z" clipRule="evenodd" />
              </svg>
            </button>
            {showReactions && (
              <div className="absolute bottom-full left-0 mb-1 z-20 flex items-center gap-1 bg-white rounded-full shadow-lg border border-gray-100 px-2 py-1.5">
                {QUICK_REACTIONS.map(e => (
                  <button key={e} onClick={() => { onReact(msg.id, e); setShowReactions(false) }}
                    className="text-lg hover:scale-125 transition-transform leading-none p-0.5">{e}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function Chats({ currentUserId, requests, selectedRequestId, setSelectedRequestId, messages, setMessages, socket, typingUsers, unreadCounts = {}, onMarkChatRead, onOpenProfile, onOpenReel, groupChats = [], setGroupChats, onRefreshGroupChats }) {
  const accepted = requests.filter(r => r.status === 'ACCEPTED')
  const activeReq = accepted.find(r => r.id === selectedRequestId)
  const chatMessages = (activeReq && messages[activeReq.id]) || []
  const otherUser = activeReq
    ? (activeReq.senderId === currentUserId ? activeReq.receiver : activeReq.sender)
    : null
  const [messageText, setMessageText] = useState('')
  const [loadingMsgs, setLoadingMsgs] = useState(false)
  const [loadingOlderMsgs, setLoadingOlderMsgs] = useState(false)
  const [hasMoreMsgsMap, setHasMoreMsgsMap] = useState({})
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)  // message being replied to
  const [pendingFile, setPendingFile] = useState(null) // { file, previewUrl, mediaType, fileName }
  const [uploading, setUploading] = useState(false)
  const [showChatMenu, setShowChatMenu] = useState(false)
  const [chatMenuConfirm, setChatMenuConfirm] = useState(null) // 'block' | 'unblock' | 'report' | 'reportblock'
  const [chatMenuLoading, setChatMenuLoading] = useState(false)
  const [blockedUsers, setBlockedUsers] = useState({}) // { [userId]: bool }

  // ── Group event chat state ────────────────────────────────
  const [activeGroupChat, setActiveGroupChat] = useState(null) // { type, eventId, tableName, cuisineEmoji, otherUserId?, otherUserName?, otherUserPicture?, isHost? }
  const [groupMsgs, setGroupMsgs] = useState([])
  const [loadingGroupMsgs, setLoadingGroupMsgs] = useState(false)
  const [groupMsgText, setGroupMsgText] = useState('')
  const [sendingGroupMsg, setSendingGroupMsg] = useState(false)
  const groupMsgEndRef = useRef(null)

  const chatMenuRef = useRef(null)
  const msgContainerRef = useRef(null)
  const typingTimerRef = useRef(null)
  const fileInputRef = useRef(null)
  const emojiPickerRef = useRef(null)
  const loadingOlderRef = useRef(false)
  const prependCountRef = useRef(0)
  const loadOlderFnRef  = useRef(null)

  const messageVirtualizer = useVirtualizer({
    count: chatMessages.length,
    getScrollElement: () => msgContainerRef.current,
    estimateSize: () => 80,
    overscan: 10,
  })

  // Fetch messages on demand when a conversation is selected
  useEffect(() => {
    if (!selectedRequestId) return
    if (messages[selectedRequestId]) return
    prependCountRef.current = 0
    setLoadingMsgs(true)
    apiFetch(`/messages/${selectedRequestId}?limit=30`)
      .then(r => r.json())
      .then(({ messages: data, hasMore }) => {
        setMessages(prev => ({ ...prev, [selectedRequestId]: Array.isArray(data) ? data : [] }))
        setHasMoreMsgsMap(prev => ({ ...prev, [selectedRequestId]: Boolean(hasMore) }))
      })
      .catch(() => setMessages(prev => ({ ...prev, [selectedRequestId]: [] })))
      .finally(() => setLoadingMsgs(false))
  }, [selectedRequestId])

  useEffect(() => {
    if (!chatMessages.length || !msgContainerRef.current) return
    const count = prependCountRef.current
    if (count > 0) {
      // Older messages were prepended — restore scroll to the message that was previously first
      prependCountRef.current = 0
      const frame = requestAnimationFrame(() => {
        messageVirtualizer.scrollToIndex(count, { align: 'start', behavior: 'instant' })
      })
      return () => cancelAnimationFrame(frame)
    }
    // New message arrived or conversation opened — scroll to bottom
    const frame = requestAnimationFrame(() => {
      messageVirtualizer.scrollToIndex(chatMessages.length - 1, { align: 'end' })
    })
    return () => cancelAnimationFrame(frame)
  }, [chatMessages.length, selectedRequestId])

  useEffect(() => {
    if (activeReq?.id && socket) socket.emit('join_room', activeReq.id)
  }, [activeReq?.id, socket])

  // Load older messages when user scrolls near the top
  async function loadOlderMessages() {
    if (!selectedRequestId || !hasMoreMsgsMap[selectedRequestId] || loadingOlderRef.current) return
    loadingOlderRef.current = true
    setLoadingOlderMsgs(true)
    try {
      const oldest = chatMessages[0]
      const res = await apiFetch(`/messages/${selectedRequestId}?before=${oldest.id}&limit=30`)
      if (res.ok) {
        const { messages: older, hasMore } = await res.json()
        if (older.length > 0) {
          prependCountRef.current = older.length
          setMessages(prev => ({
            ...prev,
            [selectedRequestId]: [...older, ...(prev[selectedRequestId] || [])]
          }))
        }
        setHasMoreMsgsMap(prev => ({ ...prev, [selectedRequestId]: Boolean(hasMore) }))
      }
    } finally {
      loadingOlderRef.current = false
      setLoadingOlderMsgs(false)
    }
  }

  // Keep loadOlderMessages ref current so the scroll handler never goes stale
  useEffect(() => { loadOlderFnRef.current = loadOlderMessages })

  // Attach scroll listener — re-attaches when conversation changes
  useEffect(() => {
    const el = msgContainerRef.current
    if (!el || !selectedRequestId) return
    const onScroll = () => {
      if (el.scrollTop < 150) loadOlderFnRef.current?.()
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [selectedRequestId])

  // Fetch block status for the other user whenever the active chat changes
  useEffect(() => {
    if (!activeReq || !currentUserId) return
    const otherUser = activeReq.senderId === currentUserId ? activeReq.receiver : activeReq.sender
    if (blockedUsers[otherUser.id] !== undefined) return // already fetched
    apiFetch(`/users/${otherUser.id}/block`)
      .then(r => r.json())
      .then(data => setBlockedUsers(prev => ({ ...prev, [otherUser.id]: Boolean(data.blocked) })))
      .catch(() => {})
  }, [activeReq?.id])

  // Close emoji picker on outside click
  useEffect(() => {
    function handler(e) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false)
      }
      if (chatMenuRef.current && !chatMenuRef.current.contains(e.target)) {
        setShowChatMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function handleInput(e) {
    setMessageText(e.target.value)
    if (!socket || !activeReq) return
    socket.emit('typing', { requestId: activeReq.id })
    clearTimeout(typingTimerRef.current)
    typingTimerRef.current = setTimeout(() => {
      socket.emit('stop_typing', { requestId: activeReq.id })
    }, 1500)
  }

  function handleEmojiSelect(emojiData) {
    setMessageText(prev => prev + emojiData.emoji)
    setShowEmojiPicker(false)
  }

  function handleFileSelect(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const isImage = file.type.startsWith('image/')
    const previewUrl = isImage ? URL.createObjectURL(file) : null
    setPendingFile({
      file,
      previewUrl,
      mediaType: isImage ? 'image' : 'file',
      fileName: file.name,
    })
    // reset input so same file can be re-selected
    e.target.value = ''
  }

  function removePendingFile() {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl)
    setPendingFile(null)
  }

  async function handleSend() {
    if ((!messageText.trim() && !pendingFile) || !socket || !activeReq) return
    const otherUser = activeReq.senderId === currentUserId ? activeReq.receiver : activeReq.sender

    let mediaUrl = null, mediaType = null, fileName = null

    if (pendingFile) {
      setUploading(true)
      try {
        const form = new FormData()
        form.append('file', pendingFile.file)
        const res = await apiUpload('/messages/upload', form)
        if (!res.ok) throw new Error('Upload failed')
        const data = await res.json()
        mediaUrl = data.url
        mediaType = data.mediaType
        fileName = data.fileName
      } catch (err) {
        console.error(err)
        setUploading(false)
        return
      } finally {
        setUploading(false)
        removePendingFile()
      }
    }

    socket.emit('send_message', {
      requestId: activeReq.id,
      receiverId: otherUser.id,
      text: messageText.trim() || null,
      mediaUrl,
      mediaType,
      fileName,
      replyToId: replyingTo?.id ?? null,
    })
    socket.emit('stop_typing', { requestId: activeReq.id })
    clearTimeout(typingTimerRef.current)
    setMessageText('')
    setReplyingTo(null)
  }

  const handleReact = useCallback((messageId, emoji) => {
    if (!socket || !activeReq) return
    socket.emit('react_message', { messageId, emoji, requestId: activeReq.id })
  }, [socket, activeReq])

  const handleReply = useCallback((msg) => {
    setReplyingTo(msg)
  }, [])

  // ── Group event chat helpers ──────────────────────────────
  async function openGroupChat(chat) {
    setActiveGroupChat(chat)
    setSelectedRequestId(null)
    setGroupMsgs([])
    setLoadingGroupMsgs(true)
    try {
      const url = chat.type === 'dm'
        ? `/group/${chat.eventId}/messages?dmWith=${chat.otherUserId}&limit=60`
        : `/group/${chat.eventId}/messages?type=group&limit=60`
      const res = await apiFetch(url)
      if (res.ok) {
        const data = await res.json()
        setGroupMsgs(Array.isArray(data.messages) ? data.messages : [])
      }
    } catch {}
    finally { setLoadingGroupMsgs(false) }
    socket?.emit('join_group_room', chat.eventId)
  }

  async function handleSendGroupMsg() {
    if (!activeGroupChat || !groupMsgText.trim()) return
    setSendingGroupMsg(true)
    try {
      const body = { text: groupMsgText.trim() }
      if (activeGroupChat.type === 'dm') body.recipientUserId = activeGroupChat.otherUserId
      const res = await apiFetch(`/group/${activeGroupChat.eventId}/messages`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (res.ok) {
        const data = await res.json()
        if (data.message) setGroupMsgs(prev => [...prev, data.message])
        setGroupMsgText('')
      }
    } catch {}
    finally { setSendingGroupMsg(false) }
  }

  // Socket: real-time group messages
  useEffect(() => {
    if (!socket) return
    function handleNewGroupMsg(msg) {
      // Update latest message preview in sidebar
      setGroupChats(prev => prev.map(chat => {
        const isGroup = chat.type === 'group' && chat.eventId === msg.groupEventId && !msg.recipientUserId
        const isDm = chat.type === 'dm' && chat.eventId === msg.groupEventId &&
          (chat.otherUserId === msg.senderId || chat.otherUserId === msg.recipientUserId)
        if (!isGroup && !isDm) return chat
        return {
          ...chat,
          latestMessage: {
            id: msg.id,
            text: msg.text,
            senderName: msg.sender?.name || 'Member',
            senderId: msg.senderId,
            createdAt: msg.createdAt,
          }
        }
      }))
      // Add to active group chat if it matches
      if (!activeGroupChat) return
      if (msg.groupEventId !== activeGroupChat.eventId) return
      const isForActive = activeGroupChat.type === 'dm'
        ? msg.recipientUserId !== null && (msg.senderId === currentUserId || msg.recipientUserId === currentUserId) &&
          (msg.senderId === activeGroupChat.otherUserId || msg.recipientUserId === activeGroupChat.otherUserId)
        : msg.recipientUserId === null
      if (!isForActive) return
      setGroupMsgs(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg])
    }
    socket.on('new_group_message', handleNewGroupMsg)
    return () => socket.off('new_group_message', handleNewGroupMsg)
  }, [socket, activeGroupChat, currentUserId])

  // Scroll to bottom of group chat on new messages
  useEffect(() => {
    groupMsgEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [groupMsgs.length])

  // Leave group room when chat changes
  useEffect(() => {
    return () => {
      if (activeGroupChat) socket?.emit('leave_group_room', activeGroupChat.eventId)
    }
  }, [activeGroupChat?.eventId])

  const isOtherTyping = activeReq ? Boolean(typingUsers[activeReq.id]) : false
  const connected = Boolean(socket?.connected)

  const unifiedChats = useMemo(() => {
    const groupItems = groupChats.map((chat) => {
      const key = chat.type === 'group' ? `g-${chat.eventId}` : `dm-${chat.eventId}-${chat.otherUserId}`
      const latestAt = chat.latestMessage?.createdAt || chat.dateTime || chat.updatedAt || null
      const preview = chat.type === 'dm'
        ? `Private · ${chat.tableName}`
        : (chat.latestMessage
            ? `${chat.latestMessage.senderName}: ${chat.latestMessage.text}`
            : 'Group dining chat')

      return {
        kind: 'group',
        key,
        latestAt,
        title: chat.type === 'group' ? chat.tableName : chat.otherUserName,
        subtitle: preview,
        avatarEmoji: chat.cuisineEmoji,
        selected: activeGroupChat?.eventId === chat.eventId &&
          (chat.type === 'group' ? activeGroupChat.type === 'group' : activeGroupChat.otherUserId === chat.otherUserId),
        onOpen: () => openGroupChat(chat),
      }
    })

    const directItems = accepted.map((req) => {
      const user = req.senderId === currentUserId ? req.receiver : req.sender
      const chatMsgs = messages[req.id] || []
      const lastMsg = chatMsgs.at(-1)
      const isTyping = Boolean(typingUsers[req.id])

      let preview = ''
      if (isTyping) preview = 'typing…'
      else if (lastMsg?.mediaType === 'image') preview = '📷 Photo'
      else if (lastMsg?.mediaType === 'file') preview = `📎 ${lastMsg.fileName || 'File'}`
      else preview = lastMsg?.text || user.location?.area || ''

      return {
        kind: 'direct',
        key: `d-${req.id}`,
        latestAt: lastMsg?.createdAt || req.updatedAt || req.createdAt || null,
        requestId: req.id,
        user,
        isTyping,
        preview,
        unread: unreadCounts[req.id] || 0,
        selected: selectedRequestId === req.id && !activeGroupChat,
        onOpen: () => {
          setSelectedRequestId(req.id)
          setActiveGroupChat(null)
          onMarkChatRead?.(req.id)
        },
      }
    })

    return [...groupItems, ...directItems].sort((a, b) => {
      const at = a.latestAt ? new Date(a.latestAt).getTime() : 0
      const bt = b.latestAt ? new Date(b.latestAt).getTime() : 0
      return bt - at
    })
  }, [
    accepted,
    activeGroupChat,
    currentUserId,
    groupChats,
    messages,
    onMarkChatRead,
    selectedRequestId,
    typingUsers,
    unreadCounts,
  ])

  async function handleChatMenuAction(action, otherUserId) {
    setChatMenuLoading(true)
    try {
      if (action === 'block') {
        await apiFetch(`/users/${otherUserId}/block`, { method: 'POST' })
        setBlockedUsers(prev => ({ ...prev, [otherUserId]: true }))
      } else if (action === 'unblock') {
        await apiFetch(`/users/${otherUserId}/block`, { method: 'DELETE' })
        setBlockedUsers(prev => ({ ...prev, [otherUserId]: false }))
      } else if (action === 'report') {
        await apiFetch(`/users/${otherUserId}/report`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ blockAsWell: false }) })
      } else if (action === 'reportblock') {
        await apiFetch(`/users/${otherUserId}/report`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ blockAsWell: true }) })
        setBlockedUsers(prev => ({ ...prev, [otherUserId]: true }))
      }
    } catch (err) {
      console.error(err)
    } finally {
      setChatMenuLoading(false)
      setChatMenuConfirm(null)
      setShowChatMenu(false)
    }
  }

  return (
    <div className="flex gap-4 h-full">

      {/* ── Conversation list ── */}
      <div className={`fade-up glass-card rounded-3xl border border-white/60 p-4 shadow-xl flex flex-col ${(activeReq || activeGroupChat) ? 'hidden sm:flex sm:w-64 shrink-0' : 'flex-1'}`}>
        <h2 className="title-font text-lg font-bold text-gray-900 mb-3 shrink-0">Chats</h2>

        {unifiedChats.length === 0 ? (
          <p className="text-sm text-gray-600">No active chats yet.</p>
        ) : (
          <div className="flex-1 overflow-y-auto -mr-1 pr-1 space-y-2">
            {unifiedChats.map((entry) => (
              entry.kind === 'group' ? (
                <button
                  key={entry.key}
                  onClick={entry.onOpen}
                  className={`w-full rounded-2xl border-2 p-3 text-left transition ${entry.selected ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-lg shrink-0">
                      {entry.avatarEmoji}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{entry.title}</p>
                      <p className="text-xs text-gray-500 truncate">{entry.subtitle}</p>
                    </div>
                  </div>
                </button>
              ) : (
                <button
                  key={entry.key}
                  onClick={entry.onOpen}
                  className={`w-full rounded-2xl border-2 p-3 text-left transition ${entry.selected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative shrink-0">
                      <img src={entry.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(entry.user.name)}`}
                        className="h-10 w-10 rounded-xl object-cover bg-green-100" alt={entry.user.name} />
                      {entry.isTyping && (
                        <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 ring-2 ring-white">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm truncate ${entry.unread ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>{entry.user.name}</p>
                      <p className={`text-xs truncate ${entry.isTyping ? 'text-green-600 italic font-medium' : 'text-gray-500'}`}>
                        {entry.preview}
                      </p>
                    </div>
                    {entry.unread > 0 && (
                      <span className="shrink-0 rounded-full bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[1.2rem] text-center">
                        {entry.unread > 99 ? '99+' : entry.unread}
                      </span>
                    )}
                  </div>
                </button>
              )
            ))}
          </div>
        )}
      </div>

      {/* ── Group event chat window ── */}
      {activeGroupChat && (
        <div className="fade-up flex-1 glass-card rounded-3xl border border-white/60 shadow-xl flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
            <button onClick={() => setActiveGroupChat(null)} className="sm:hidden text-gray-500 mr-1 text-lg">←</button>
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-xl shrink-0">
              {activeGroupChat.cuisineEmoji}
            </div>
            <div className="flex-1 min-w-0">
              <p className="title-font font-bold text-gray-900 truncate">
                {activeGroupChat.type === 'group' ? activeGroupChat.tableName : activeGroupChat.otherUserName}
              </p>
              <p className="text-xs text-gray-500">
                {activeGroupChat.type === 'group' ? `Group dining · ${activeGroupChat.tableName}` : `Personal message · ${activeGroupChat.tableName}`}
              </p>
            </div>
            <button
              onClick={() => { setActiveGroupChat(null); onRefreshGroupChats?.() }}
              className="text-xs text-gray-400 hover:text-gray-600 shrink-0"
              title="Close"
            >✕</button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50/60 space-y-2">
            {loadingGroupMsgs ? (
              <p className="text-center text-xs text-gray-500 mt-8">Loading messages…</p>
            ) : groupMsgs.length === 0 ? (
              <p className="text-center text-xs text-gray-500 mt-8">No messages yet. Start the conversation.</p>
            ) : (
              groupMsgs.map(msg => {
                const isMine = msg.senderId === currentUserId
                return (
                  <div key={`gm-${msg.id}`} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isMine ? 'bg-orange-500 text-white rounded-br-sm' : 'bg-white text-gray-900 shadow-sm rounded-bl-sm'}`}>
                      {!isMine && (
                        <p className="text-xs font-semibold text-orange-600 mb-1">
                          {msg.sender?.name || 'Member'}
                          {msg.recipientUserId ? ` → ${msg.recipient?.name || 'You'}` : ''}
                        </p>
                      )}
                      <p>{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${isMine ? 'text-orange-200' : 'text-gray-400'}`}>
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                )
              })
            )}
            <div ref={groupMsgEndRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-gray-200 bg-white/80 backdrop-blur-sm rounded-b-3xl flex items-end gap-2 px-4 py-3">
            <textarea
              rows={1}
              value={groupMsgText}
              onChange={e => setGroupMsgText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendGroupMsg() } }}
              className="flex-1 rounded-2xl border border-orange-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none max-h-32"
              placeholder={activeGroupChat.type === 'group' ? 'Message the group…' : `Message ${activeGroupChat.otherUserName}…`}
              style={{ minHeight: '46px' }}
            />
            <button
              onClick={handleSendGroupMsg}
              disabled={!groupMsgText.trim() || sendingGroupMsg}
              className="rounded-full bg-orange-500 w-11 h-11 flex items-center justify-center text-white hover:bg-orange-600 disabled:opacity-40 transition shrink-0"
            >
              {sendingGroupMsg
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 -rotate-45">
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                  </svg>
              }
            </button>
          </div>
        </div>
      )}

      {/* ── Active 1:1 chat window ── */}
      {!activeGroupChat && activeReq && (() => {
        const iBlockedThem = otherUser ? Boolean(blockedUsers[otherUser.id]) : false
        return (
          <div className="fade-up flex-1 glass-card rounded-3xl border border-white/60 shadow-xl flex flex-col min-h-0 overflow-hidden">

            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
              <button onClick={() => setSelectedRequestId(null)} className="sm:hidden text-gray-500 mr-1 text-lg">←</button>
              <button
                onClick={() => onOpenProfile?.(otherUser.id)}
                className="shrink-0"
                title={`Open ${otherUser.name} profile`}
              >
                <img src={otherUser.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(otherUser.name)}`}
                  className="h-10 w-10 rounded-xl object-cover" alt={otherUser.name} />
              </button>
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => onOpenProfile?.(otherUser.id)}
                  className="title-font font-bold text-gray-900 hover:text-orange-700 transition-colors"
                >
                  {otherUser.name}
                </button>
                <p className={`text-xs transition-all ${isOtherTyping ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>
                  {isOtherTyping ? 'typing…' : (otherUser.location?.area || '')}
                </p>
              </div>
              {connected
                ? <span className="flex items-center gap-1.5 text-xs text-green-600 font-semibold shrink-0"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />Live</span>
                : <span className="text-xs text-gray-400 shrink-0">Connecting…</span>
              }

              {/* Three-dot menu */}
              <div className="relative shrink-0" ref={chatMenuRef}>
                <button
                  onClick={() => { setShowChatMenu(v => !v); setChatMenuConfirm(null) }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                  title="Options"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>

                {showChatMenu && !chatMenuConfirm && (
                  <div className="absolute top-full right-0 mt-1 z-30 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden min-w-[170px] py-1">
                    {iBlockedThem ? (
                      <button
                        onClick={() => setChatMenuConfirm('unblock')}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-green-700 hover:bg-green-50 transition-colors"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                        Unblock
                      </button>
                    ) : (
                      <button
                        onClick={() => setChatMenuConfirm('block')}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524L13.477 14.89zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" /></svg>
                        Block
                      </button>
                    )}
                    <button
                      onClick={() => setChatMenuConfirm('report')}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 transition-colors"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0"><path fillRule="evenodd" d="M3 6a3 3 0 013-3h10l-1.5 3L16 9H6a1 1 0 00-1 1v7H3V6z" clipRule="evenodd" /></svg>
                      Report
                    </button>
                    {!iBlockedThem && (
                      <button
                        onClick={() => setChatMenuConfirm('reportblock')}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524L13.477 14.89zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" /></svg>
                        Report &amp; Block
                      </button>
                    )}
                  </div>
                )}

                {showChatMenu && chatMenuConfirm && (
                  <div className="absolute top-full right-0 mt-1 z-30 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 min-w-[220px]">
                    <p className="text-sm font-semibold text-gray-800 mb-1">
                      {chatMenuConfirm === 'block' && 'Block this user?'}
                      {chatMenuConfirm === 'unblock' && 'Unblock this user?'}
                      {chatMenuConfirm === 'report' && 'Report this user?'}
                      {chatMenuConfirm === 'reportblock' && 'Report and block?'}
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      {chatMenuConfirm === 'block' && 'They would not be able to message you.'}
                      {chatMenuConfirm === 'unblock' && 'They will be able to message you again.'}
                      {chatMenuConfirm === 'report' && 'This will flag them for review.'}
                      {chatMenuConfirm === 'reportblock' && 'They would be flagged and would not be able to message you.'}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleChatMenuAction(chatMenuConfirm, otherUser.id)}
                        disabled={chatMenuLoading}
                        className="flex-1 py-1.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-semibold transition disabled:opacity-60"
                      >
                        {chatMenuLoading ? 'Please wait…' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => { setChatMenuConfirm(null); setShowChatMenu(false) }}
                        className="flex-1 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Messages — scrollable middle */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-gray-50/60">
              {loadingOlderMsgs && (
                <div className="shrink-0 py-2 text-center text-xs text-gray-400 border-b border-gray-100">
                  Loading older messages…
                </div>
              )}
              <div ref={msgContainerRef} className="flex-1 overflow-y-auto p-4">
              {loadingMsgs
                ? <p className="text-center text-xs text-gray-500 mt-8">Loading messages…</p>
                : chatMessages.length === 0
                ? <p className="text-center text-xs text-gray-500 mt-8">No messages yet. Say hi! 👋</p>
                : (
                  <div style={{ height: `${messageVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                    {messageVirtualizer.getVirtualItems().map(vItem => {
                      const msg = chatMessages[vItem.index]
                      const isMine = msg.senderId === currentUserId
                      const next = chatMessages[vItem.index + 1]
                      const isLastInGroup = !next || next.senderId !== msg.senderId
                      return (
                        <div
                          key={vItem.key}
                          data-index={vItem.index}
                          ref={messageVirtualizer.measureElement}
                          style={{ position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${vItem.start}px)`, paddingBottom: '0.5rem' }}
                        >
                          <MessageBubble
                            msg={msg}
                            isMine={isMine}
                            otherUser={otherUser}
                            currentUserId={currentUserId}
                            onReact={handleReact}
                            onReply={handleReply}
                            onOpenReel={onOpenReel}
                            isLastInGroup={isLastInGroup}
                          />
                        </div>
                      )
                    })}
                  </div>
                )
              }

              {isOtherTyping && (
                <div className="flex items-end gap-1.5 justify-start mt-2">
                  <img src={otherUser.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(otherUser.name)}`}
                    className="h-6 w-6 rounded-lg object-cover bg-orange-100 shrink-0" alt="" />
                  <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex gap-1 items-center">
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '160ms' }} />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '320ms' }} />
                  </div>
                </div>
              )}
            </div>
            </div>

            {/* Input area */}
            <div className="shrink-0 border-t border-gray-200 bg-white/80 backdrop-blur-sm rounded-b-3xl">

              {/* Blocked banner */}
              {iBlockedThem && (
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border-b border-red-100">
                  <div className="flex items-center gap-2 text-red-600">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524L13.477 14.89zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" /></svg>
                    <span className="text-xs font-semibold">You blocked this user</span>
                  </div>
                  <button
                    onClick={() => handleChatMenuAction('unblock', otherUser.id)}
                    className="text-xs text-red-600 underline hover:text-red-800 font-medium shrink-0"
                  >
                    Unblock
                  </button>
                </div>
              )}

              {/* Pending file preview */}
              {pendingFile && (
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                  {pendingFile.mediaType === 'image'
                    ? <img src={pendingFile.previewUrl} className="h-14 w-14 rounded-xl object-cover border border-gray-200" alt="preview" />
                    : <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2">
                        <span className="text-2xl">📎</span>
                        <span className="text-xs text-gray-700 max-w-[160px] truncate">{pendingFile.fileName}</span>
                      </div>
                  }
                  <button onClick={removePendingFile} className="text-gray-400 hover:text-red-500 text-lg ml-1">✕</button>
                </div>
              )}

              {/* Reply preview strip */}
              {replyingTo && (
                <div className="flex items-center gap-2 px-4 pt-2 pb-1 border-b border-orange-100 bg-orange-50/60">
                  <div className="flex-1 border-l-2 border-orange-400 pl-2">
                    <span className="text-xs font-semibold text-orange-700 block">
                      {replyingTo.senderId === currentUserId ? 'You' : otherUser.name}
                    </span>
                    <span className="text-xs text-gray-600 truncate block max-w-[300px]">
                      {replyingTo.mediaType === 'image' ? '📷 Photo' :
                       replyingTo.mediaType === 'file'  ? `📎 ${replyingTo.fileName || 'File'}` :
                       replyingTo.text}
                    </span>
                  </div>
                  <button onClick={() => setReplyingTo(null)} className="text-gray-400 hover:text-red-500 text-sm">✕</button>
                </div>
              )}

              {/* Text input row */}
              <div className="flex items-end gap-2 px-4 py-3">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.zip,.txt,video/mp4,audio/mpeg"
                  className="hidden"
                  onChange={handleFileSelect}
                />

                {/* Attachment button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!connected || uploading || iBlockedThem}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 disabled:opacity-40 transition shrink-0"
                  title="Attach file or image"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                  </svg>
                </button>

                {/* Emoji picker wrapper */}
                <div className="relative shrink-0" ref={emojiPickerRef}>
                  <button
                    onClick={() => setShowEmojiPicker(v => !v)}
                    disabled={!connected || iBlockedThem}
                    className="text-gray-400 hover:text-orange-500 text-xl disabled:opacity-40 transition pb-2"
                    title="Emoji"
                  >
                    😊
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-12 left-0 z-30">
                      <EmojiPicker
                        onEmojiClick={handleEmojiSelect}
                        height={350}
                        width={300}
                        searchDisabled={false}
                        skinTonesDisabled
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  )}
                </div>

                <textarea
                  rows={1}
                  placeholder={iBlockedThem ? 'Unblock to send messages' : connected ? 'Type a message…' : 'Connecting to chat…'}
                  disabled={!connected || uploading || iBlockedThem}
                  value={messageText}
                  onChange={handleInput}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
                  className="flex-1 rounded-2xl border border-orange-200 bg-white px-4 py-3 text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none max-h-32 overflow-y-auto"
                  style={{ minHeight: '46px' }}
                />

                <button
                  onClick={handleSend}
                  disabled={(!messageText.trim() && !pendingFile) || !connected || uploading || iBlockedThem}
                  className="rounded-full bg-green-600 w-11 h-11 flex items-center justify-center text-white hover:bg-green-700 disabled:opacity-40 transition shrink-0"
                >
                  {uploading
                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 -rotate-45">
                        <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                      </svg>
                  }
                </button>
              </div>
            </div>

          </div>
        )
      })()}
    </div>
  )
}

